import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Redirect, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import BackLink from '../shared/BackLink';
import CategoryHeader from './CategoryHeader';
import CategoryProduct from './CategoryProduct';
import cartUtils from '../../../utils/cart';
import { doesCategoryContainsMetallicProducts } from '../../../utils/misc';
import { isParentProduct } from '../../../utils/misc';
import { isMultiConfigProduct } from '../../../utils/misc';
import {
  isShippingRestrictedForParent,
  isProductAllowed,
  isCategoryRestricted as computeCategoryRestricted,
} from '../helpers/internationalRestriction';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 50%; // 1:2 Aspect Ratio

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    object-fit: contain;
  }
`;

const ContentList = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`;

// Status badges shared with Offerings (kept inline rather than extracted —
// only two call sites today). If a third surface appears, lift to a shared
// component module.
const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
`;

const RestrictedBadge = styled(Badge)`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
`;

const RestrictedWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0.55;
  pointer-events: none;
  cursor: not-allowed;
`;

const Category = (props) => {
  const { match, history } = props;
  const categoryId = match.params.id;

  const [category, setCategory] = useState(null);
  const [productId, setProductId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [isNthFree, setIsNthFree] = useState(false);
  const [
    categoryContainsMetallicProducts,
    setCategoryContainsMetallicProducts,
  ] = useState(false);
  const [metallicProductsFilterActive, setMetallicProductsFilterActive] =
    useState(false);

  const availablePoses = useSelector(
    (state) => state.additionalData.availablePoses,
  );
  const selectedStudent = useSelector(
    (state) => state.additionalData.selectedStudent,
  );
  const shootProducts = useSelector((state) => state.shoot.shootProducts);
  const defaultPaperType = useSelector(
    (state) => state.shoot?.shoot?.pricing_model?.default_paper_type,
  );
  const orders = useSelector((state) => state.v3Order.orders);
  const shootEventId = useSelector((state) => {
    const s = state.shoot.shoot;
    return s?.event_id || s?.id;
  });
  const parent = useSelector((state) => state.parent);
  const shootData = useSelector((state) => state.shoot.shoot);
  const restricted = isShippingRestrictedForParent(shootData, parent);

  useEffect(() => {
    let categoryToUpdate;

    const firstCategoryProduct = category?.products?.[0];

    // console.log('firstCategoryProduct', firstCategoryProduct);
    // console.log('isParentProduct', isParentProduct(firstCategoryProduct));
    // console.log(
    //   'isMultiConfigProduct',
    //   isMultiConfigProduct(firstCategoryProduct),
    // );

    if (
      isParentProduct(firstCategoryProduct) &&
      !isMultiConfigProduct(firstCategoryProduct)
    ) {
      setRedirectUrl(`/v3/products/${productId}/product/0#slots`);
      return;
    }

    if (
      isParentProduct(firstCategoryProduct) &&
      isMultiConfigProduct(firstCategoryProduct)
    ) {
      setRedirectUrl(`/v3/products-regular/${productId}`);
      return;
    }

    // If categoryId is 'nth-free', find the nth-free category
    if (categoryId === 'nth-free') {
      categoryToUpdate = shootProducts.find(
        (cat) => cat.category_type === 'nth_free',
      );
      setIsNthFree(true);
    } else {
      // Find the category that matches the id in the url
      categoryToUpdate = shootProducts.find(
        (cat) => cat.id.toString() === categoryId,
      );
    }

    if (categoryToUpdate?.products?.[0]?.id) {
      setProductId(categoryToUpdate.products[0].id);
    }

    if (doesCategoryContainsMetallicProducts(categoryToUpdate?.products)) {
      setCategoryContainsMetallicProducts(true);

      if (defaultPaperType === 'metallic') {
        setMetallicProductsFilterActive(true);
      }
    }

    setCategory(categoryToUpdate);

    if (categoryId === 'nth-free') {
      Swal.fire(
        'Congratulations, you qualify for a free item!',
        'Please configure your free item',
      );
    }

    setIsLoading(false);
  }, [props.hasPoses, productId, categoryId]);

  if (redirectUrl) {
    return <Redirect to={redirectUrl} />;
  }

  // TODO: v3 - add a proper loading spinner here
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // get array of unlocked groups for lockable products
  const orderKey =
    shootEventId && selectedStudent?.id
      ? `${shootEventId}-${selectedStudent.id}`
      : null;
  const cartItems = (orderKey ? orders[orderKey] : undefined)?.cartItems || [];
  const unlockedGroups = cartUtils.unlockedGroups(cartItems);
  // For nth free products, use lowest qualifier price (equal or lesser value rule)
  const lowestPriceNthFreeQualifier =
    cartUtils.lowestPriceNthFreeQualifier(cartItems);
  // Keep highest for backward compatibility if needed elsewhere
  const highestPriceNthFreeQualifier =
    cartUtils.highestPriceNthFreeQualifier(cartItems);

  let childCategories = [];

  // If the current category has child categories, find them
  if (
    category &&
    category.child_category_ids &&
    category.child_category_ids.length > 0
  ) {
    childCategories = shootProducts.filter((potentialChildCategory) =>
      category.child_category_ids.includes(potentialChildCategory.id),
    );
  }

  const categoryProducts = category?.products
    .filter((product) => {
      // Apply the nth_free_value filter only if isNthFree is true
      // Use lowest qualifier price for "equal or lesser value" rule
      if (isNthFree && product.nth_free_value > highestPriceNthFreeQualifier) {
        return false;
      }

      // Filter child_products from displaying within the category itself
      // (needed for multi_config products)
      if (product.product_type === 'child_product') {
        return false;
      }

      // Hide shippable products when international shipping is blocked for this parent
      if (!isProductAllowed(product, restricted)) {
        return false;
      }

      // If the metallic product filter is active, only include products that are metallic
      if (metallicProductsFilterActive) {
        return product.is_metallic === true;
      }
      // Otherwise, exclude products that are metallic
      return product.is_metallic !== true;
    })
    .map((product) => (
      <CategoryProduct
        key={product.id}
        product={product}
        unlockedGroups={unlockedGroups}
        isNthFree={isNthFree}
      />
    ));

  const childCategoryProductsDisplay = childCategories.map((subCategory) => {
    const product = subCategory?.products?.[0];
    const subRestricted = computeCategoryRestricted(
      subCategory,
      shootProducts,
      restricted,
    );
    const className = `bnl-product-group bnl-product-group--alternate col-xs-12 col-md-6 col-lg-6 ${
      subRestricted ? '' : 'cursor-pointer'
    }`;

    const subCardBody = (
      <>
        {!subRestricted && (
          <Link
            to={`/v3/categories/${subCategory.id}`}
            className="categoryLink"
          />
        )}
        <h3 className="bnl-product-group__header">{subCategory.name}</h3>
        <div className="bnl-product-group__subheader">
          {subCategory.subheader}
        </div>
        <div className="bnl-product-group__image-container">
          <ImageContainer>
            <img
              className="bnl-product-group__image--single"
              src={subCategory.image_url}
            />
          </ImageContainer>
        </div>
        {product?.is_multi_config === true && (
          <>
            <p className="bnl-product-regular__desc">{product?.description}</p>

            <ContentList>
              {product?.contents.map((content) => {
                return <p>{content}</p>;
              })}
            </ContentList>
          </>
        )}
      </>
    );

    return (
      <li key={subCategory.id} className={className}>
        {restricted && subRestricted && (
          <RestrictedBadge>Not available internationally</RestrictedBadge>
        )}
        {subRestricted ? (
          <RestrictedWrap>{subCardBody}</RestrictedWrap>
        ) : (
          subCardBody
        )}
      </li>
    );
  });

  // If the current category has child categories, display them
  if (childCategories.length > 0) {
    return (
      <div className="bnl-product-groups-container">
        <BackLink />
        <div className="clearfix">
          <h2
            className="bnl-product-groups__header"
            style={{ paddingTop: 0, marginTop: 0 }}
          >
            {category.name}
          </h2>
        </div>
        <ul className="bnl-product-groups row">
          {childCategoryProductsDisplay}
        </ul>
      </div>
    );
  }

  // Render the products for the current category
  return (
    <div className="bnl-product-groups-container">
      {!isNthFree && <BackLink />}
      <CategoryHeader
        key={category?.id}
        isNthFree={isNthFree}
        categoryName={category?.name}
        categoryContainsMetallicProducts={categoryContainsMetallicProducts}
        highestPriceNthFreeQualifier={highestPriceNthFreeQualifier}
        metallicProductsFilterActive={metallicProductsFilterActive}
        setMetallicProductsFilterActive={setMetallicProductsFilterActive}
      />
      <ul className="bnl-product-groups row">{categoryProducts}</ul>
    </div>
  );
};

export default withRouter(Category);
